import { Trans } from "@lingui/react/macro";

import { ActivationRecoveryPanel } from "@/components/auth/activation-recovery-panel";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  mapAccessErrorToRecoveryState,
  type AccessRecoveryState,
} from "@/integrations/multiresume/access/access-surface";

type ProtectedCapabilityRecoveryProps = {
  error: unknown;
  safeReturnTarget: string;
};

/**
 * Renders only after an authoritative protected-operation response.  The
 * response code, not a client guess, decides whether activation or support is
 * the next safe action.
 */
export function ProtectedCapabilityRecovery({ error, safeReturnTarget }: ProtectedCapabilityRecoveryProps) {
  return (
    <ProtectedCapabilityRecoveryState
      state={mapAccessErrorToRecoveryState(error)}
      safeReturnTarget={safeReturnTarget}
    />
  );
}

export function ProtectedCapabilityRecoveryState({
  state,
  safeReturnTarget,
}: {
  state: AccessRecoveryState;
  safeReturnTarget: string;
}) {
  if (state === "none") return null;

  if (state === "activation-required") {
    return (
      <Alert data-phase6-state="activation-required">
        <AlertTitle>
          <Trans>Activate your account to continue</Trans>
        </AlertTitle>
        <AlertDescription>
          <Trans>This protected action is paused until you confirm your activation email.</Trans>
          <ActivationRecoveryPanel activationState="registered" safeReturnTarget={safeReturnTarget} />
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert data-phase6-state="account-disabled" variant="destructive">
      <AlertTitle>
        <Trans>This account is disabled</Trans>
      </AlertTitle>
      <AlertDescription>
        <Trans>Protected actions are unavailable. Contact support if you think this is a mistake.</Trans>
        <p className="mt-3">
          <a
            className="font-medium underline underline-offset-4"
            href="mailto:support@multiresu.me?subject=Account%20access%20help"
          >
            <Trans>Contact support</Trans>
          </a>
        </p>
      </AlertDescription>
    </Alert>
  );
}
